import { FC, HTMLAttributes } from "react";
import { useStyles } from "./ActivityCard.styles";
import { Typography } from "../../core/Typography/Typography";
import { Image } from "../../core/Image/Image";
import { TypographyVariant } from "../../../constants/typography";
import { Activity } from "@onmo/graphql/graphql";
import { Time } from "../../../models/time";
import LbDaily from "../../../assets/icon/activity/activity_daily_1x.webp";
import LbDaily2x from "../../../assets/icon/activity/activity_daily_2x.webp";
import LbDaily3x from "../../../assets/icon/activity/activity_daily_3x.webp";
import LbMonthly from "../../../assets/icon/activity/activity_monthly_1x.webp";
import LbMonthly2x from "../../../assets/icon/activity/activity_monthly_2x.webp";
import LbMonthly3x from "../../../assets/icon/activity/activity_monthly_3x.webp";
import LbWeekly from "../../../assets/icon/activity/activity_weekly_1x.webp";
import LbWeekly2x from "../../../assets/icon/activity/activity_weekly_2x.webp";
import LbWeekly3x from "../../../assets/icon/activity/activity_weekly_3x.webp";
import videIcon from "../../../assets/icon/activity/video_icon_1x.webp";
import videIcon2x from "../../../assets/icon/activity/video_icon_2x.webp";
import videIcon3x from "../../../assets/icon/activity/video_icon_3x.webp";
import newIcon from "../../../assets/icon/activity/news_icon_1x.webp";
import newIcon2x from "../../../assets/icon/activity/news_icon_2x.webp";
import newIcon3x from "../../../assets/icon/activity/news_icon_3x.webp";
import { useTranslation } from "react-i18next";

interface IActivityCardProps {
  activity: Activity;
}

export const ActivityCard: FC<
  IActivityCardProps & HTMLAttributes<HTMLDivElement>
> = ({ activity }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isBattleActivity =
    activity.data?.__typename === "BattleActivityData" ||
    activity.data?.__typename === "GameplayEndedActivityData";

  const convertActivity = () => {
    const data = activity.data;
    switch (data?.__typename) {
      case "BattleActivityData": {
        const gameData = JSON.parse(data.game.metadata || "{}");
        return {
          title: t("common_Battle"),
          description: data.game.title,
          src1x: gameData?.gameImages?.small || gameData?.gameImages?.small_ca,
          src2x: gameData?.gameImages?.small2x || gameData?.gameImages?.small_ca2x,
          src3x: gameData?.gameImages?.small3x || gameData?.gameImages?.small_ca3x,
          createdAt: activity.createdAt,
        };
      }
      case "CompleteViewContentActivityData": {
        const isVideo = !data.contentType || data.contentType === 'video';
        return {
          title: isVideo ? t("common_Video") : t("common_News"),
          description: activity.product?.name,
          src1x: isVideo ? videIcon : newIcon,
          src2x: isVideo ? videIcon2x : newIcon2x,
          src3x: isVideo ? videIcon3x : newIcon3x,
          createdAt: activity.createdAt,
        };
      }
      case "GameplayEndedActivityData": {
        const gameData = JSON.parse(data.gamePlay?.game?.metadata || "{}");
        return {
          title: t("common_Battle"),
          description: data.gamePlay?.game?.title,
          src1x: gameData?.gameImages?.small || gameData?.gameImages?.small_ca,
          src2x: gameData?.gameImages?.small2x || gameData?.gameImages?.small_ca2x,
          src3x: gameData?.gameImages?.small3x || gameData?.gameImages?.small_ca3x,
          createdAt: activity.createdAt,
        };
      }
      case "GameEndedActivityData": {
        const productIcon = (activity.product as { icon?: string } | null)
          ?.icon;
        return {
          title: activity.product?.name,
          src1x: productIcon,
          src2x: productIcon,
          src3x: productIcon,
          createdAt: activity.createdAt,
        };
      }
      case "UserLeaderboardActivityData": {
        if (data.repeatCycle === "daily") {
          return {
            title: `${t("common_Daily")} ${t("common_Leaderboard")}`,
            description: "#" + data.leaderboardRank,
            src1x: LbDaily,
            src2x: LbDaily2x,
            src3x: LbDaily3x,
            createdAt: activity.createdAt,
          };
        } else if (data.repeatCycle === "weekly") {
          return {
            title: `${t("common_Weekly")} ${t("common_Leaderboard")}`,
            description: "#" + data.leaderboardRank,
            src1x: LbWeekly,
            src2x: LbWeekly2x,
            src3x: LbWeekly3x,
            createdAt: activity.createdAt,
          };
        } else {
          return {
            title: `${t("common_Monthly")} ${t("common_Leaderboard")}`,
            description: "#" + data.leaderboardRank,
            src1x: LbMonthly,
            src2x: LbMonthly2x,
            src3x: LbMonthly3x,
            createdAt: activity.createdAt,
          };
        }
      }
      default:
        return null;
    }
  };

  const convertedActivity = convertActivity();

  if (!convertedActivity) {
    return null;
  }

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <Image
          className={`${classes.image} ${
            isBattleActivity ? classes.battleImage : ""
          }`}
          url={convertedActivity.src1x}
          url2x={convertedActivity.src2x}
          url3x={convertedActivity.src3x}
        />
        <div className={classes.contentText}>
          <Typography variant={TypographyVariant.BodyB5Bold}>
            {convertedActivity.title}
          </Typography>
          <Typography variant={TypographyVariant.BodyB6Medium}>
            {convertedActivity.description}
          </Typography>
        </div>
      </div>
      <Typography variant={TypographyVariant.BodyB6Regular}>
        {Time.convertTimestampToFormattedDate(
          convertedActivity.createdAt,
          true,
        )}
      </Typography>
    </div>
  );
};
